/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { When } from "react-if";
import { Button, theme } from "@nordcloud/gnui";
import { Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { Item } from "~/components/SidebarWithMultiSelect/types";
import { useGetAllActions } from "~/hooks/useGetAllActions";
import { useGetPlans } from "~/views/plans/hooks";
import { TimelineFilterState } from "../types";

type Props = {
  filters: TimelineFilterState;
  onApply: (filters: TimelineFilterState) => void;
  onClear: () => void;
};

export function TimelineFiltersSidebar({ filters, onApply, onClear }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<TimelineFilterState>(filters);

  const { data: plans } = useGetPlans({
    page: "1",
    limit: "100",
    basic: true,
  });

  const { customActions, systemActions } = useGetAllActions();

  const allActions: Item[] = [
    ...systemActions.map((a) => ({ label: a.name, value: a.id })),
    ...customActions.map((a) => ({ label: a.name, value: a.id })),
  ];

  const planItems: Item[] = plans.map((p) => ({ label: p.name, value: p.id }));

  const actionTypeItems: Item[] = [
    { label: "System Actions", value: "SYSTEM" },
    { label: "Custom Actions", value: "CUSTOM" },
  ];

  const open = () => {
    setState(filters);
    setIsOpen(true);
  };

  const close = () => setIsOpen(false);

  const hasPendingChanges = JSON.stringify(state) !== JSON.stringify(filters);

  const applyFilters = () => {
    onApply(state);
    close();
  };

  const clearAllFilters = () => {
    const emptyState: TimelineFilterState = {
      planIds: [],
      actionTypes: [],
      actionIds: [],
      resourceGroupIds: [],
    };
    setState(emptyState);
    onClear();
    close();
  };

  const hasActiveFilters =
    filters.planIds.length > 0 ||
    filters.actionTypes.length > 0 ||
    filters.actionIds.length > 0 ||
    filters.resourceGroupIds.length > 0;

  const resultsText = hasPendingChanges ? "Apply to see results" : "";

  return (
    <>
      <Button
        severity={hasActiveFilters ? "medium" : "low"}
        size="sm"
        mr={theme.spacing.spacing04}
        icon="filter"
        onClick={open}
        data-testid="timeline-filters-button"
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Timeline"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={resultsText}
              isApplyDisabled={!hasPendingChanges}
              isClearDisabled={!hasActiveFilters}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <FilterWrapper
            name="Plans"
            isSelected={state.planIds.length > 0}
            selectedOptions={state.planIds}
            onClear={() => setState((s) => ({ ...s, planIds: [] }))}
          >
            <SelectableListWithSearch
              selectableItems={planItems}
              selectedOptions={state.planIds}
              onMultiSelect={(selected: string[]) =>
                setState((s) => ({ ...s, planIds: selected }))
              }
            />
          </FilterWrapper>
          <FilterWrapper
            name="Action Type"
            isSelected={state.actionTypes.length > 0}
            selectedOptions={state.actionTypes}
            onClear={() => setState((s) => ({ ...s, actionTypes: [] }))}
          >
            <SelectableListWithSearch
              selectableItems={actionTypeItems}
              selectedOptions={state.actionTypes}
              onMultiSelect={(selected: string[]) =>
                setState((s) => ({ ...s, actionTypes: selected }))
              }
            />
          </FilterWrapper>
          <FilterWrapper
            name="Action Name"
            isSelected={state.actionIds.length > 0}
            selectedOptions={state.actionIds}
            onClear={() => setState((s) => ({ ...s, actionIds: [] }))}
          >
            <SelectableListWithSearch
              selectableItems={allActions}
              selectedOptions={state.actionIds}
              onMultiSelect={(selected: string[]) =>
                setState((s) => ({ ...s, actionIds: selected }))
              }
            />
          </FilterWrapper>
        </Sidebar>
      </When>
    </>
  );
}
